/**
 * PLAYER domain state.
 *
 * ============================================================================
 * NO AUDIO IS PLAYED. There is no <audio> element and no audio file. "Playback"
 * is an interval incrementing `elapsed` until it reaches the song's parsed length,
 * driven by usePlayer(). The waveform is a deterministic pseudo-random bar chart
 * seeded from the song id, not real amplitude data.
 *
 * `volume` and `muted` are therefore inert as far as *sound* is concerned: the controls
 * move and persist, but nothing audible responds to them. They are retained
 * deliberately — the UI for them already exists and Phase 5 wires them to a real audio
 * element. Mute is the one whose STATE is nonetheless fully real; see the note on
 * `muted` below.
 *
 * `speed` and `repeat` are NOT in that category. Both change what playback does, because
 * both are read by the clock in useSimulatedPlayback: `repeat` loops the track instead of
 * ending it, and `speed` sets the tick period to `1000 / speed`, so a 3:12 track finishes
 * in 1:36 at 2× and the position, the clock, the seek fill and the timeline playhead all
 * move at the scaled rate. What speed cannot do is alter a pitch, there being nothing to
 * pitch — see src/lib/playback.ts, which is also where its value goes to
 * `audio.playbackRate` when an element exists.
 * ============================================================================
 */
import { create } from 'zustand';

interface PlayerState {
  /** The song loaded in the player, or null. Loaded is not the same as running. */
  songId: string | null;
  /** Real media duration when an audio element has one; null otherwise. */
  mediaDurationSec: number | null;
  /** Seconds buffered from the start of the current real media, or 0 when unknown. */
  bufferedSec: number;
  /** True while a real audio-backed track is loading or buffering. */
  loading: boolean;
  /** True after Play was requested and before the startup buffer threshold is reached. */
  pendingStart: boolean;
  /**
   * True when a song is loaded but the clock is not running.
   *
   * WHY THIS EXISTS. Until playlists needed it, `songId: null` meant both "nothing is
   * loaded" and "paused", and pausing therefore threw away the position (`elapsed`
   * back to 0) and the play order (`queue` emptied). That is fine for one track — stop
   * and pause are the same gesture on a single song — and wrong for a playlist, where
   * pausing track 3 of 8 has to leave you on track 3 of 8. There was no state that
   * could say so.
   *
   * So the two facts are separate now: `songId` is what is loaded, `paused` is whether
   * it is advancing. Nothing else about the store changed shape — the queue is still
   * derived from `songId` (see below), because a loaded-but-paused song is still a
   * position in the play order.
   *
   * Only meaningful while `songId` is set, and cleared alongside it, so the flag can
   * never linger on an empty player.
   */
  paused: boolean;
  /** Version within the song, when a specific version is selected. */
  versionId: string | null;
  /**
   * A Version History play-button run that is intentionally NOT loaded into the transport.
   *
   * The Studio now has two different version-play gestures:
   *   - Load / card click: make the version the transport's loaded source (`songId` + `versionId`)
   *   - Play button only: audition the version in the background without retargeting the
   *     transport UI or highlighting the row as the loaded one
   *
   * These fields track that second mode only. They do not carry elapsed/duration because the
   * Version History rows only need to know which version is running and whether its button
   * should read Play or Pause; the transport deliberately stays focused elsewhere.
   */
  backgroundVersionSongId: string | null;
  backgroundVersionId: string | null;
  backgroundVersionPaused: boolean;
  /** Seconds elapsed. Advanced by usePlayer's interval, at `speed` × real time. */
  elapsed: number;
  /**
   * The Mix tool's live preview, when the shared audio element is temporarily playing it.
   *
   * THIS IS NOT A SECOND PLAYER. The preview hijacks the one shared audio element and then
   * gives it back; these fields are only the state the Studio transport needs while that is
   * true — which song's Mix panel owns the preview, where the preview playhead is, and
   * whether it is paused. The ordinary player fields above are left intact so the original
   * song/queue position can be restored after the preview ends.
   */
  previewSongId: string | null;
  /** Seconds elapsed within the live Mix preview. */
  previewElapsed: number;
  /** Real runtime of the preview audio, when metadata has loaded. */
  previewDurationSec: number | null;
  /** True while the preview audio is paused. */
  previewPaused: boolean;
  /** 0–100. Inert — see the file header. */
  volume: number;
  /**
   * Playback rate. 0.5 | 1 | 1.5 | 2 from the selector; see data/music PLAYBACK_SPEEDS.
   *
   * REAL, in the sense the app has of playback: it is the tick rate of the clock in
   * useSimulatedPlayback, so it changes how long a track takes and how fast every
   * position on screen moves. Stored unclamped — this is a record of what was chosen —
   * and clamped where it is divided by, in lib/playback. See the file header.
   */
  speed: number;
  /** Loops the current track instead of ending it. Honoured by the clock. */
  repeat: boolean;
  /**
   * True while output is muted.
   *
   * A flag ALONGSIDE `volume` rather than `volume: 0`, which is the whole point of
   * the feature. Muting by zeroing the slider destroys the level the user had chosen,
   * so unmuting has nothing to restore and has to guess — and the slider itself jumps
   * to the far left and back, which reads as the control having been reset.
   *
   * Here `volume` keeps its value throughout and this flag decides whether it is
   * heard. That also makes the two independent in the way people expect: adjusting
   * the slider while muted sets the level you will hear when you unmute, and it is
   * `setVolume` that decides whether dragging it up should unmute (it does).
   *
   * `volume: 0` is a legitimate separate state — the slider dragged to the bottom —
   * and it is deliberately not the same thing as muted. See `toggleMute`.
   */
  muted: boolean;
  /**
   * The play order a playlist put the player into, as song ids.
   *
   * Empty when playback was started from a single track — a library row, the Studio
   * transport — which is the overwhelming majority of playback and needs no queue.
   *
   * THE CURRENT POSITION IS NOT STORED. It is `queue.indexOf(songId)`, derived on
   * read, and that is deliberate: an index and a `songId` are two facts that can
   * disagree, and every one of the ways they can (a song deleted out from under the
   * queue, a playlist reordered mid-playback, a single-track play landing on a song
   * that happens to be in the queue) would leave the transport's next/previous
   * pointing somewhere the user is not. With one fact there is nothing to reconcile.
   *
   * `queue` names ids rather than holding songs for the reason `Playlist.songIds`
   * does; see the type's note.
   */
  queue: string[];

  /**
   * Plays the given song, or pauses/resumes it when it is already the loaded one.
   *
   * TWO DIFFERENT GESTURES, and separating them is the whole of the playlist fix.
   *
   * Pressing this on the song that is already loaded is pause/resume: `songId`,
   * `elapsed` and `queue` all stay exactly as they were, so resuming carries on from
   * where it stopped and a playlist is still a playlist afterwards. It used to clear
   * all three, which is why pausing track 3 of 8 ended the playlist.
   *
   * Pressing it on a DIFFERENT song abandons the queue, which is unchanged: this is
   * the single-track path — a library row, the Studio's transport — and starting one
   * track by hand is leaving the playlist you were listening to, not inserting into it.
   */
  toggle: (songId: string) => void;
  play: (songId: string, versionId?: string | null) => void;
  /**
   * Cues a song paused at 0:00 without starting it.
   *
   * The song counterpart of `loadVersion`: it makes the track the loaded source so a
   * transport can seek it or inspect its duration before playback starts.
   */
  load: (songId: string) => void;
  /**
   * The version-aware counterpart of `toggle`, for a version row's Play button.
   *
   * Keyed on the (song, version) PAIR rather than the song alone, which is the whole
   * difference. Pressing it on the version that is already loaded is pause/resume, exactly
   * as `toggle` is for a song: `elapsed` and `queue` stay, so it carries on from where it
   * stopped. Pressing it on a DIFFERENT version of the same song — or a different song —
   * starts that version from the top, because switching versions is not a resume of the
   * one you were on, it is a new position.
   */
  toggleVersion: (songId: string, versionId: string) => void;
  /**
   * Cues a version PAUSED at 0:00 without playing it — what the "Load" menu item does.
   *
   * `loadVersion` is to `toggleVersion` what a cue is to a play: it makes the version the
   * loaded one, so the transport shows its runtime and the scrubber spans it, but leaves
   * `paused` set so nothing advances until play is pressed. Starts at zero and abandons the
   * queue, the single-track rule `play` follows — loading a version is a fresh position,
   * not a step within a playlist.
   */
  loadVersion: (songId: string, versionId: string) => void;
  /** Marks a Version History background audition as the currently playing version. */
  setBackgroundVersion: (songId: string, versionId: string) => void;
  /** Marks that background audition as paused or running. */
  setBackgroundVersionPaused: (paused: boolean) => void;
  /** Clears the current Version History background audition state. */
  clearBackgroundVersion: () => void;
  /**
   * Starts a play order at `startIndex`, so the tracks after it follow on.
   *
   * The one way a queue is created. Playlist playback is this plus the advance in
   * hooks/usePlayer's clock — there is no second player and no second position.
   */
  playQueue: (songIds: string[], startIndex?: number) => void;
  /**
   * Moves to the next/previous song in the queue. A no-op at the ends.
   *
   * Carries the paused flag across rather than forcing playback: skipping while
   * paused leaves you paused on the new track, which is what every player does and
   * what someone lining up a track before pressing play means.
   */
  playNext: () => void;
  playPrevious: () => void;
  /**
   * Pauses playback unless `songId` is already the loaded song. Keeps position and queue.
   *
   * A screen that displays one track and renders `elapsed` as that track's position —
   * the Studio — must not leave a different song advancing behind it, or the clock, the
   * playhead and the waveform would all be attributing another track's motion to the one
   * on screen. So arriving there pauses, and this is that rule.
   *
   * The exception is the whole reason it takes an argument. When the loaded song IS the
   * one being opened there is no discrepancy to correct, and pausing would instead break
   * two things that should work: opening the Studio on the track you are listening to
   * would silence it, and a queue advancing INTO a new page would stop on arrival.
   *
   * A no-op on an empty player and idempotent, so a caller does not have to check first.
   */
  pauseOther: (songId: string) => void;
  /** Drops a song from the queue without disturbing what is playing. */
  dropFromQueue: (songId: string) => void;
  /**
   * Empties the player: nothing loaded, no position, no queue.
   *
   * Distinct from pausing, which is `toggle` on the loaded song. Stop is what the
   * clock does at the end of a queue and what deleting the playing song does — both
   * cases where there is no longer a position to hold on to.
   */
  stop: () => void;
  setElapsed: (elapsed: number) => void;
  setMediaDurationSec: (duration: number | null) => void;
  setBufferedSec: (seconds: number) => void;
  setLoading: (loading: boolean) => void;
  setPendingStart: (pending: boolean) => void;
  setVolume: (volume: number) => void;
  /**
   * Sets the playback rate. Takes effect immediately, mid-track, without moving the
   * position — see the note on `speed` and useSimulatedPlayback.
   */
  setSpeed: (speed: number) => void;
  toggleRepeat: () => void;
  /** Mutes if unmuted, and restores the previous level if muted. */
  toggleMute: () => void;
  /** Marks a Mix preview as active for the given song. */
  startPreview: (songId: string) => void;
  /** Updates the preview playhead. */
  setPreviewElapsed: (elapsed: number) => void;
  /** Records the preview runtime when metadata is known. */
  setPreviewDurationSec: (duration: number | null) => void;
  /** Marks the preview as paused or running. */
  setPreviewPaused: (paused: boolean) => void;
  /** Clears the active Mix preview. */
  clearPreview: () => void;
  /** Stops playback if the given song is the one playing. Used on delete. */
  stopIfPlaying: (songId: string) => void;
  reset: () => void;
}

const INITIAL = {
  songId: null,
  mediaDurationSec: null,
  bufferedSec: 0,
  loading: false,
  pendingStart: false,
  paused: false,
  versionId: null,
  backgroundVersionSongId: null,
  backgroundVersionId: null,
  backgroundVersionPaused: false,
  elapsed: 0,
  previewSongId: null,
  previewElapsed: 0,
  previewDurationSec: null,
  previewPaused: false,
  volume: 80,
  speed: 1,
  repeat: false,
  muted: false,
  queue: [] as string[],
};

/**
 * The level an unmute falls back to when there is nothing to restore.
 *
 * Reachable one way: mute, then drag the slider to zero, then unmute. Zeroing while
 * muted is a deliberate act, but leaving the user unmuted *and* silent gives them a
 * control that appears to have done nothing — so the unmute lands somewhere audible.
 * The same default the store starts at, so it is not a new number.
 */
const FALLBACK_VOLUME = INITIAL.volume;

/**
 * The state of a player with nothing in it.
 *
 * Named once because three actions produce it — `stop`, `stopIfPlaying` and `reset`
 * via INITIAL — and a fourth would be tempting to write by hand. Every field that
 * describes *what is loaded* is here, so adding another (a version, a position) is
 * one edit rather than a hunt for the ones that were missed.
 */
const EMPTY = {
  songId: null,
  mediaDurationSec: null,
  bufferedSec: 0,
  loading: false,
  pendingStart: false,
  paused: false,
  versionId: null,
  backgroundVersionSongId: null,
  backgroundVersionId: null,
  backgroundVersionPaused: false,
  elapsed: 0,
  previewSongId: null,
  previewElapsed: 0,
  previewDurationSec: null,
  previewPaused: false,
  queue: [] as string[],
};

export const usePlayerStore = create<PlayerState>((set, get) => ({
  ...INITIAL,

  toggle(songId) {
    /*
      Pause and resume, when it is the loaded song. Only `paused` moves: `elapsed`
      stays so resuming continues rather than restarting, and `queue` stays so the
      playlist you were listening to is still the playlist you are on.

      This is the line the playlist defect lived on. It used to clear songId, elapsed
      and queue in both directions — sound for one track, where pause and stop really
      are the same gesture, and wrong the moment there is a play order to lose.
    */
    if (get().songId === songId) {
      set((s) => ({
        paused: !s.paused,
        pendingStart: s.paused ? true : false,
        backgroundVersionSongId: null,
        backgroundVersionId: null,
        backgroundVersionPaused: false,
      }));
      return;
    }
    // A different song: the single-track path, which abandons the queue as it always
    // has. Starting one track by hand is leaving the playlist, not inserting into it.
    set({
      songId,
      paused: false,
      pendingStart: true,
      bufferedSec: 0,
      versionId: null,
      backgroundVersionSongId: null,
      backgroundVersionId: null,
      backgroundVersionPaused: false,
      elapsed: 0,
      queue: [],
    });
  },

  play(songId, versionId = null) {
    set({
      songId,
      paused: false,
      pendingStart: true,
      bufferedSec: 0,
      versionId,
      backgroundVersionSongId: null,
      backgroundVersionId: null,
      backgroundVersionPaused: false,
      elapsed: 0,
      queue: [],
    });
  },

  load(songId) {
    set({
      songId,
      paused: true,
      pendingStart: false,
      bufferedSec: 0,
      versionId: null,
      backgroundVersionSongId: null,
      backgroundVersionId: null,
      backgroundVersionPaused: false,
      elapsed: 0,
      queue: [],
    });
  },

  toggleVersion(songId, versionId) {
    const { songId: loadedSong, versionId: loadedVersion } = get();
    /*
      Pause/resume only when BOTH the song and the version already match — the same
      "it is the loaded thing" test `toggle` makes, tightened to the version. Only
      `paused` moves, so `elapsed` and `queue` survive and resuming continues rather
      than restarting.
    */
    if (loadedSong === songId && loadedVersion === versionId) {
      set((s) => ({
        paused: !s.paused,
        pendingStart: s.paused ? true : false,
        backgroundVersionSongId: null,
        backgroundVersionId: null,
        backgroundVersionPaused: false,
      }));
      return;
    }
    // A different version (or song): start it from the top. Switching versions is not a
    // resume, so the position resets and the queue is abandoned exactly as `play` does.
    set({
      songId,
      paused: false,
      pendingStart: true,
      bufferedSec: 0,
      versionId,
      backgroundVersionSongId: null,
      backgroundVersionId: null,
      backgroundVersionPaused: false,
      elapsed: 0,
      queue: [],
    });
  },

  loadVersion(songId, versionId) {
    // Cued, not playing: `paused: true` with `pendingStart: false` so the clock stays put
    // while the transport shows the version's runtime. Zeroed and queue-cleared like
    // `play`, because loading a version is a fresh single-track position, not a resume.
    set({
      songId,
      paused: true,
      pendingStart: false,
      bufferedSec: 0,
      versionId,
      backgroundVersionSongId: null,
      backgroundVersionId: null,
      backgroundVersionPaused: false,
      elapsed: 0,
      queue: [],
    });
  },

  setBackgroundVersion(songId, versionId) {
    set({ backgroundVersionSongId: songId, backgroundVersionId: versionId, backgroundVersionPaused: false });
  },

  setBackgroundVersionPaused(backgroundVersionPaused) {
    set({ backgroundVersionPaused });
  },

  clearBackgroundVersion() {
    const { backgroundVersionSongId, backgroundVersionId, backgroundVersionPaused } = get();
    if (!backgroundVersionSongId && !backgroundVersionId && !backgroundVersionPaused) return;
    set({ backgroundVersionSongId: null, backgroundVersionId: null, backgroundVersionPaused: false });
  },

  playQueue(songIds, startIndex = 0) {
    const songId = songIds[startIndex];
    // An empty playlist, or an index past its end, is not something to fail on: the
    // caller is a button whose playlist may have been emptied in another tab.
    if (!songId) return;
    set({
      songId,
      paused: false,
      pendingStart: true,
      bufferedSec: 0,
      versionId: null,
      backgroundVersionSongId: null,
      backgroundVersionId: null,
      backgroundVersionPaused: false,
      elapsed: 0,
      queue: [...songIds],
    });
  },

  playNext() {
    const { queue, songId } = get();
    const next = queue[queue.indexOf(songId ?? '') + 1];
    // Stops at the end rather than wrapping, matching the Studio's own Prev/Next,
    // which are absent at the ends for the same reason: in a five-track playlist,
    // "next" meaning "back to the first" is not what a transport bar means.
    if (!next) return;
    // `paused` is deliberately not touched: skipping while paused leaves you paused
    // on the new track. The clock only advances a queue while running, so the
    // auto-advance path arrives here with `paused` already false.
    set({
      songId: next,
      pendingStart: true,
      bufferedSec: 0,
      versionId: null,
      backgroundVersionSongId: null,
      backgroundVersionId: null,
      backgroundVersionPaused: false,
      elapsed: 0,
    });
  },

  playPrevious() {
    const { queue, songId } = get();
    const at = queue.indexOf(songId ?? '');
    // -1 would index the last entry, so the first position is checked explicitly
    // rather than relying on the lookup coming back undefined.
    if (at <= 0) return;
    set({
      songId: queue[at - 1],
      pendingStart: true,
      bufferedSec: 0,
      versionId: null,
      backgroundVersionSongId: null,
      backgroundVersionId: null,
      backgroundVersionPaused: false,
      elapsed: 0,
    });
  },

  pauseOther(songId) {
    const loaded = get().songId;
    // The `loaded &&` is not redundant with the inequality: `paused` is documented as
    // meaningful only while something is loaded, and setting it on an empty player
    // would produce the one state that note rules out.
    if (loaded && loaded !== songId) set({ paused: true });
  },

  dropFromQueue(songId) {
    const { queue } = get();
    if (!queue.includes(songId)) return;
    set({ queue: queue.filter((id) => id !== songId) });
  },

  stop() {
    set({ ...EMPTY });
  },

  setElapsed(elapsed) {
    set({ elapsed });
  },

  setMediaDurationSec(mediaDurationSec) {
    set({ mediaDurationSec });
  },

  setBufferedSec(bufferedSec) {
    set({ bufferedSec });
  },

  setLoading(loading) {
    set({ loading });
  },

  setPendingStart(pendingStart) {
    set({ pendingStart });
  },

  setVolume(volume) {
    const next = Math.max(0, Math.min(100, volume));
    /*
      Raising the slider unmutes. Moving a volume control is an unambiguous request
      to hear something, and a slider that could be dragged to full while output
      stayed silent would look broken with no way to tell why.

      Dragging it to zero does NOT set `muted` — that is the slider at its bottom,
      which is a state of its own. Turning it back up from there restores sound
      without any mute involvement, so nothing is lost by keeping the two apart.
    */
    set(next > 0 ? { volume: next, muted: false } : { volume: next });
  },

  setSpeed(speed) {
    set({ speed });
  },

  toggleRepeat() {
    set((s) => ({ repeat: !s.repeat }));
  },

  toggleMute() {
    const { muted, volume } = get();
    /*
      Only the flag moves. `volume` is untouched in both directions, which is what
      makes "restore the previous level" nothing more than clearing the flag — there
      is no stashed copy to keep in sync, and no path on which the stash and the
      slider can disagree.

      The one adjustment is unmuting into silence: see FALLBACK_VOLUME.
    */
    if (muted) set({ muted: false, volume: volume > 0 ? volume : FALLBACK_VOLUME });
    else set({ muted: true });
  },

  startPreview(songId) {
    set({ previewSongId: songId, previewElapsed: 0, previewDurationSec: null, previewPaused: false });
  },

  setPreviewElapsed(previewElapsed) {
    set({ previewElapsed });
  },

  setPreviewDurationSec(previewDurationSec) {
    set({ previewDurationSec });
  },

  setPreviewPaused(previewPaused) {
    set({ previewPaused });
  },

  clearPreview() {
    const { previewSongId, previewElapsed, previewDurationSec, previewPaused } = get();
    if (!previewSongId && previewElapsed === 0 && previewDurationSec === null && !previewPaused) return;
    set({ previewSongId: null, previewElapsed: 0, previewDurationSec: null, previewPaused: false });
  },

  stopIfPlaying(songId) {
    // A full clear, including while paused: the caller is a delete, and holding a
    // position in a song that no longer exists is the state this guards against.
    if (get().songId === songId) set({ ...EMPTY });
  },

  reset() {
    set({ ...INITIAL });
  },
}));
